import React, { useEffect, useState } from 'react';
import { useData } from '../contexts/DataContext';
import { sendTelegramTestMessage, updateProfile, verifyNftHolder, requestNftChallenge } from '../services/creatorService';
import { initDiscordConnect, initTelegramConnect, initTwitterReplacement, getDiscordLoginUrl } from '../services/authService';
import { signWalletProof } from '../services/walletProof';
import Toggle from '../components/ui/Toggle';
import apiClient from '../services/apiClient';
import useMediaQuery from '../hooks/useMediaQuery';

const shortenAddress = (addr) => (addr && addr.length > 10 ? `${addr.slice(0, 6)}…${addr.slice(-4)}` : addr || '');

const DISCORD_ERROR_MESSAGES = {
  discord_denied: 'You declined the Discord authorization.',
  discord_missing_params: 'Discord did not return the expected response. Please try again.',
  discord_invalid_state: 'Your Discord link request expired. Please try again.',
  discord_already_linked: 'This Discord account is already linked to another creator.',
  discord_creator_not_found: 'Your account could not be found. Please log in again.',
  discord_failed: 'Discord connection failed. Please try again.',
  x_replace_invalid_state: 'Your X replacement request expired. Sign in with Discord and try again.',
  x_replace_failed: 'X replacement could not be completed. If the new account already has a profile, ask support to review it. Your existing history has been preserved.'
};

const NFT_ERROR_MESSAGES = {
  DISCORD_NOT_CONNECTED: 'Connect Discord first, then try verifying again.',
  MISSING_HOLDER_ROLE: 'Your Discord account does not have the Bricktopian NFT holder role yet. Verify with Vulcan in Discord, then try again.',
  ROLE_CHECK_FAILED: 'Discord could not be reached for the holder role check. Try again in a moment.',
  ROLE_NOT_CONFIGURED: 'Discord holder verification is not available right now. Please contact the team.',
  WALLET_PROOF_EXPIRED: 'Wallet verification expired. Try again to request a new signature.',
  WALLET_PROOF_INVALID: 'Sign with the NFT wallet address you entered.',
};

export default function SettingsPage() {
  const { getCachedProfile, getStale, invalidate } = useData();
  const stale = getStale('profile');
  const [profile, setProfile] = useState(stale);
  const [loading, setLoading] = useState(!stale);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [disconnecting, setDisconnecting] = useState(null);
  const [connecting, setConnecting] = useState(null);

  // NFT-holder verification panel state
  const [nftPanelOpen, setNftPanelOpen] = useState(false);
  const [nftAddress, setNftAddress] = useState('');
  const [nftLoading, setNftLoading] = useState(false);
  const [nftError, setNftError] = useState(null);

  const isMobile = useMediaQuery('(max-width: 767px)');

  const [walletAddress, setWalletAddress] = useState(stale?.wallet?.address || '');
  const [displayName, setDisplayName] = useState(stale?.name || '');
  const [location, setLocation] = useState({
    country: stale?.location?.country || '',
    region: stale?.location?.region || '',
  });

  const [notifications, setNotifications] = useState({
    campaignUpdates: true,
    paymentAlerts: true,
    weeklyDigest: false,
  });

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        setError(null);
        const p = await getCachedProfile();
        if (!mounted) return;
        setProfile(p);
        setWalletAddress(p.wallet?.address || '');
        setDisplayName(p.name || '');
        setLocation({
          country: p.location?.country || '',
          region: p.location?.region || '',
        });
        if (p.notifications) setNotifications(p.notifications);
      } catch {
        if (mounted) setError('Failed to load settings.');
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => { mounted = false; };
  }, [getCachedProfile]);

  // Handle Discord OAuth callback redirect (?connected=discord or ?error=discord_*)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const connected = params.get('connected');
    const errCode = params.get('error');
    const detail = params.get('detail');
    const status = params.get('status');
    if (!connected && !errCode) return;

    if (connected === 'discord') {
      setNotice('Discord connected successfully.');
      invalidate('profile');
      getCachedProfile(true).then((p) => setProfile(p)).catch(() => {});
      setTimeout(() => setNotice(null), 4000);
    } else if (errCode && DISCORD_ERROR_MESSAGES[errCode]) {
      const base = DISCORD_ERROR_MESSAGES[errCode];
      const suffix = [detail, status ? `HTTP ${status}` : null].filter(Boolean).join(' — ');
      setError(suffix ? `${base} (${suffix})` : base);
    }

    // Clean the query string so refreshes don't replay the toast
    window.history.replaceState({}, '', window.location.pathname);
  }, [getCachedProfile, invalidate]);

  const saveWallet = async () => {
    const address = walletAddress.trim();
    if (address && !/^0x[a-fA-F0-9]{40}$/.test(address)) {
      setError('Enter an Ethereum wallet address starting with 0x followed by 40 hexadecimal characters.');
      return;
    }
    setError(null);
    setSaving(true);
    try {
      const res = await updateProfile({ wallet: { address, network: 'Ethereum' } });
      const updated = res.data;
      setProfile(updated);
      setWalletAddress(updated.wallet?.address || '');
      invalidate('profile');
      invalidate('payments');
      invalidate('stats');
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save wallet address.');
    } finally {
      setSaving(false);
    }
  };

  const saveProfile = async () => {
    setSaving(true);
    try {
      const res = await updateProfile({ name: displayName, location });
      const updated = res.data;
      setProfile(updated);
      setDisplayName(updated.name || '');
      setLocation({
        country: updated.location?.country || '',
        region: updated.location?.region || '',
      });
      invalidate('profile');
      invalidate('activity');
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch {
      setError('Failed to save profile.');
    } finally {
      setSaving(false);
    }
  };

  const saveNotifications = async (updated) => {
    setNotifications(updated);
    try {
      const res = await updateProfile({ notifications: updated });
      setProfile(res.data);
      invalidate('profile');
    } catch {
      // silently fail for notifications
    }
  };

  const openNftPanel = () => {
    setNftPanelOpen(true);
    setNftAddress('');
    setNftError(null);
  };

  const closeNftPanel = () => {
    setNftPanelOpen(false);
    setNftAddress('');
    setNftError(null);
    setNftLoading(false);
  };

  const submitNft = async () => {
    setNftError(null);
    const trimmed = nftAddress.trim();
    if (!/^0x[a-fA-F0-9]{40}$/.test(trimmed)) {
      setNftError('Enter a valid Ethereum address (0x + 40 hex chars).');
      return;
    }
    setNftLoading(true);
    try {
      const challenge = (await requestNftChallenge(trimmed)).data;
      const signature = await signWalletProof(window.ethereum, trimmed, challenge.message);
      const res = await verifyNftHolder(trimmed, { challenge: challenge.challenge, signature });
      invalidate('profile');
      const updated = res.data?.profile || (await getCachedProfile(true));
      setProfile(updated);
      setNotice(`NFT holder verified · ${res.data?.nftCount || 0} Bricktopians. Provisional scores are refreshing.`);
      setTimeout(() => setNotice(null), 6000);
      closeNftPanel();
    } catch (err) {
      const code = err.response?.data?.code;
      const msg = NFT_ERROR_MESSAGES[code] || err.response?.data?.error ||
        (err.code === 4001 ? 'Wallet signature cancelled. You can try again when ready.' : err.message) || 'Verification failed.';
      setNftError(msg);
      setNftLoading(false);
    }
  };

  const disconnectNft = async () => {
    const confirmed = window.confirm('Remove your verified Bricktopians NFT holder status?');
    if (!confirmed) return;
    setDisconnecting('nft');
    setError(null);
    try {
      await apiClient.delete('/me/connections/nft');
      invalidate('profile');
      const updated = await getCachedProfile(true);
      setProfile(updated);
    } catch (err) {
      setError('Failed to disconnect NFT holder. Please try again.');
    } finally {
      setDisconnecting(null);
    }
  };

  const handleConnectDiscord = async () => {
    if (connecting === 'discord') return;
    setConnecting('discord');
    setError(null);
    try {
      const { data } = await initDiscordConnect();
      if (!data?.authUrl) throw new Error('No auth URL returned');
      window.location.assign(data.authUrl);
    } catch (err) {
      setError('Failed to start Discord connection. Please try again.');
      setConnecting(null);
    }
  };

  const replaceTwitter = async () => {
    if (!window.confirm('Replace the X account on your existing creator profile? Your submitted posts and payment history stay on this profile. The new X account must not already belong to another profile.')) return;
    setConnecting('twitter');
    setError(null);
    try {
      const { data } = await initTwitterReplacement();
      window.location.assign(data.authUrl);
    } catch (err) {
      if (err.response?.data?.code === 'RECENT_DISCORD_LOGIN_REQUIRED') {
        window.location.assign(getDiscordLoginUrl());
      } else {
        setError(err.response?.data?.error || 'Could not start X replacement. Please try again.');
      }
      setConnecting(null);
    }
  };

  const handleConnectTelegram = async () => {
    if (connecting === 'telegram') return;
    setConnecting('telegram');
    setError(null);
    try {
      const { data } = await initTelegramConnect();
      if (!data?.connectUrl) throw new Error('No Telegram link returned');
      const opened = window.open(data.connectUrl, '_blank', 'noopener,noreferrer');
      if (!opened) window.location.assign(data.connectUrl);
      setNotice('Telegram opened. Tap Start in the bot, then refresh this page.');
      setTimeout(() => setNotice(null), 8000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to start Telegram connection. Please try again.');
    } finally {
      setConnecting(null);
    }
  };

  const refreshTelegramStatus = async () => {
    try {
      invalidate('profile');
      const updated = await getCachedProfile(true);
      setProfile(updated);
      setNotice(updated.telegram?.dmOptIn ? 'Telegram connected.' : 'Telegram is not connected yet.');
      setTimeout(() => setNotice(null), 4000);
    } catch {
      setError('Failed to refresh Telegram status.');
    }
  };

  const handleTelegramTest = async () => {
    setConnecting('telegram-test');
    setError(null);
    try {
      await sendTelegramTestMessage();
      setNotice('Telegram test message sent.');
      setTimeout(() => setNotice(null), 4000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send Telegram test message.');
    } finally {
      setConnecting(null);
    }
  };

  const handleDisconnect = async (platform) => {
    const label = platform === 'twitter' ? 'X (Twitter)' : platform === 'telegram' ? 'Telegram' : 'Discord';
    const confirmed = window.confirm(
      `Are you sure you want to disconnect your ${label} account? You may lose access to the portal.`
    );
    if (!confirmed) return;
    setDisconnecting(platform);
    setError(null);
    try {
      await apiClient.delete(`/me/connections/${platform}`);
      invalidate('profile');
      invalidate('activity');
      const updated = await getCachedProfile(true);
      setProfile(updated);
    } catch (err) {
      setError(`Failed to disconnect ${label}. Please try again.`);
    } finally {
      setDisconnecting(null);
    }
  };

  if (loading && !profile) return <div className="loading-state">Loading...</div>;
  if (error && !profile) {
    return (
      <div className="error-state">
        <p>{error}</p>
        <button onClick={() => window.location.reload()}>Retry</button>
      </div>
    );
  }

  const p = profile || {};
  const twitterHandle = p.twitter?.username;
  const followers = p.twitter?.followers;
  const discordUsername = p.discord?.username;
  const hasDiscord = !!discordUsername;
  const telegramUsername = p.telegram?.username;
  const hasTelegram = !!p.telegram?.dmOptIn;
  const nftHolderAddress = p.wallet?.nftHolderAddress;
  const nftHolderVerified = !!p.wallet?.nftHolderVerified;
  const nftCount = p.wallet?.nftCount || 0;
  const cardPad = isMobile ? 24 : 32;
  const btnStyle = isMobile ? { width: '100%' } : {};

  return (
    <>
      <div className="sec-eyebrow">Account</div>
      <div className="sec-title">Settings</div>
      <div className="sec-sub">Manage your connected accounts and payment details.</div>

      {error && (
        <div className="portal-feedback" style={{ fontFamily: "'Space Mono', monospace", fontSize: 10, color: 'var(--red)', textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 16 }}>
          {error}
        </div>
      )}

      {notice && (
        <div className="portal-feedback" style={{ fontFamily: "'Space Mono', monospace", fontSize: 10, color: 'var(--green, #22c55e)', textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 16 }}>
          {notice}
        </div>
      )}

      <div className="sec-head">Connected Accounts</div>
      <div className="settings-card" style={{ padding: cardPad }}>
        <div className="settings-label">X (Twitter)</div>
        <div className="settings-connected" style={isMobile ? { flexWrap: 'wrap', gap: 8 } : {}}>
          <div className="settings-connected-dot" />
          <div className="settings-connected-text">
            {twitterHandle ? (
              <><strong>@{twitterHandle}</strong>{followers ? ` · ${new Intl.NumberFormat('en-US').format(followers)} followers` : ''}</>
            ) : (
              <span style={{ color: '#555' }}>Not connected</span>
            )}
          </div>
          {twitterHandle && (
            <button
              className="settings-disconnect"
              onClick={() => handleDisconnect('twitter')}
              disabled={disconnecting === 'twitter'}
              style={{ marginLeft: isMobile ? 0 : 'auto' }}
            >
              {disconnecting === 'twitter' ? 'Disconnecting...' : 'Disconnect'}
            </button>
          )}
        </div>

        <button className="settings-btn" onClick={replaceTwitter} disabled={connecting === 'twitter'} style={{ marginBottom: 20 }}>
          {connecting === 'twitter' ? 'Opening X...' : 'Replace suspended or lost X account'}
        </button>
        <div className="settings-label">Discord</div>
        <div className="settings-connected" style={isMobile ? { flexWrap: 'wrap', gap: 8 } : {}}>
          <div className="settings-connected-dot" />
          <div className="settings-connected-text">
            {discordUsername ? (
              <><strong>{discordUsername}</strong> · Connected</>
            ) : (
              <span style={{ color: '#555' }}>Not connected</span>
            )}
          </div>
          {discordUsername ? (
            <button
              className="settings-disconnect"
              onClick={() => handleDisconnect('discord')}
              disabled={disconnecting === 'discord'}
              style={{ marginLeft: isMobile ? 0 : 'auto' }}
            >
              {disconnecting === 'discord' ? 'Disconnecting...' : 'Disconnect'}
            </button>
          ) : (
            <button
              className="settings-btn"
              onClick={handleConnectDiscord}
              disabled={connecting === 'discord'}
              style={{ marginLeft: isMobile ? 0 : 'auto' }}
            >
              {connecting === 'discord' ? 'Connecting...' : 'Connect'}
            </button>
          )}
        </div>

        {p.discord?.creatorRoleStatus && (
          <p style={{ fontSize: 12, color: '#999', lineHeight: 1.6 }}>
            Creator role: {p.discord.creatorRoleStatus === 'assigned' ? 'Assigned' : 'Pending — we retry automatically'}
            {p.discord.creatorRoleError ? `. ${p.discord.creatorRoleError}` : ''}
          </p>
        )}

        <div className="settings-label">Telegram</div>
        <div className="settings-connected" style={isMobile ? { flexWrap: 'wrap', gap: 8 } : {}}>
          <div className="settings-connected-dot" />
          <div className="settings-connected-text">
            {hasTelegram ? (
              <><strong>{telegramUsername ? `@${telegramUsername}` : 'Connected'}</strong> · Notifications enabled</>
            ) : (
              <span style={{ color: '#555' }}>Not connected</span>
            )}
          </div>
          {hasTelegram ? (
            <div style={{ marginLeft: isMobile ? 0 : 'auto', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button
                className="settings-btn secondary"
                onClick={handleTelegramTest}
                disabled={connecting === 'telegram-test'}
              >
                {connecting === 'telegram-test' ? 'Sending...' : 'Send Test'}
              </button>
              <button
                className="settings-disconnect"
                onClick={() => handleDisconnect('telegram')}
                disabled={disconnecting === 'telegram'}
              >
                {disconnecting === 'telegram' ? 'Disconnecting...' : 'Disconnect'}
              </button>
            </div>
          ) : (
            <div style={{ marginLeft: isMobile ? 0 : 'auto', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button
                className="settings-btn"
                onClick={handleConnectTelegram}
                disabled={connecting === 'telegram'}
              >
                {connecting === 'telegram' ? 'Opening...' : 'Connect'}
              </button>
              <button
                className="settings-btn secondary"
                onClick={refreshTelegramStatus}
              >
                Refresh Status
              </button>
            </div>
          )}
        </div>

        <div className="settings-label">Bricktopian NFT</div>
        <div className="settings-connected" style={isMobile ? { flexWrap: 'wrap', gap: 8 } : {}}>
          <div className="settings-connected-dot" />
          <div className="settings-connected-text">
            {nftHolderVerified && nftHolderAddress ? (
              <><strong>{shortenAddress(nftHolderAddress)}</strong>{` · ${nftCount} ${nftCount === 1 ? 'NFT' : 'NFTs'}`}</>
            ) : (
              <span style={{ color: '#555' }}>Not verified</span>
            )}
          </div>
          {nftHolderVerified ? (
            <button
              className="settings-disconnect"
              onClick={disconnectNft}
              disabled={disconnecting === 'nft'}
              style={{ marginLeft: isMobile ? 0 : 'auto' }}
            >
              {disconnecting === 'nft' ? 'Removing...' : 'Disconnect'}
            </button>
          ) : (
            <button
              className="settings-btn"
              onClick={!hasDiscord ? handleConnectDiscord : nftPanelOpen ? closeNftPanel : openNftPanel}
              disabled={!hasDiscord && connecting === 'discord'}
              style={{ marginLeft: isMobile ? 0 : 'auto' }}
            >
              {!hasDiscord ? (connecting === 'discord' ? 'Connecting...' : 'Connect Discord First') : nftPanelOpen ? 'Cancel' : 'Verify'}
            </button>
          )}
        </div>

        {nftPanelOpen && !nftHolderVerified && (
          <div className="portal-reveal" style={{ marginTop: 16, padding: 16, border: '1px solid var(--border, #2a2a2a)', borderRadius: 6 }}>
            <div className="settings-label">Your NFT holder wallet address</div>
            <input
              className="settings-input"
              value={nftAddress}
              onChange={(e) => setNftAddress(e.target.value)}
              placeholder="0x..."
              disabled={nftLoading}
            />
            <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 10, color: '#777', textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 12 }}>
              Sign a wallet message to prove you control this address. We also check NFT ownership and your Discord holder role from Vulcan. The signature does not send a transaction or cost gas.
            </div>
            <button
              className="settings-btn"
              onClick={submitNft}
              disabled={nftLoading || !nftAddress.trim()}
              style={btnStyle}
            >
              {nftLoading ? 'Verifying...' : 'Verify'}
            </button>
            {nftError && (
              <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 10, color: 'var(--red)', textTransform: 'uppercase', letterSpacing: '.08em', marginTop: 12 }}>
                {nftError}
              </div>
            )}
          </div>
        )}
        {p.wallet?.nftVerificationError && <p style={{ fontSize: 12, color: '#999' }}>{p.wallet.nftVerificationError}</p>}
      </div>

      <div className="sec-head">Payment Wallet</div>
      <div className="settings-card" style={{ padding: cardPad }}>
        <div className="settings-label">Wallet Address (Ethereum — USDC)</div>
        <input
          className="settings-input"
          value={walletAddress}
          onChange={(e) => setWalletAddress(e.target.value)}
          placeholder="0x..."
        />
        <div className="settings-label">Network</div>
        <div className="settings-connected" style={{ marginBottom: 24 }}>
          <div className="settings-connected-dot" />
          <div className="settings-connected-text"><strong>Ethereum</strong> · USDC payments</div>
        </div>
        <button
          className="settings-btn"
          onClick={saveWallet}
          disabled={saving}
          style={btnStyle}
        >
          {saved ? 'Saved' : saving ? 'Saving...' : 'Save Wallet'}
        </button>
      </div>

      <div className="sec-head">Profile</div>
      <div className="settings-card" style={{ padding: cardPad }}>
        <div className="settings-label">Display Name</div>
        <input
          className="settings-input"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          placeholder="Your display name"
        />
        <div className="settings-label">Country</div>
        <input
          className="settings-input"
          value={location.country}
          onChange={(e) => setLocation(prev => ({ ...prev, country: e.target.value }))}
          placeholder="Country"
        />
        <div className="settings-label">Region</div>
        <input
          className="settings-input"
          value={location.region}
          onChange={(e) => setLocation(prev => ({ ...prev, region: e.target.value }))}
          placeholder="Region or city"
        />
        <div style={{ display: 'flex', gap: 12, flexDirection: isMobile ? 'column' : 'row' }}>
          <button
            className="settings-btn"
            onClick={saveProfile}
            disabled={saving}
            style={btnStyle}
          >
            {saved ? 'Saved' : saving ? 'Saving...' : 'Save Changes'}
          </button>
          <button
            className="settings-btn secondary"
            onClick={() => {
              invalidate('profile');
              getCachedProfile(true).then(p => {
                setProfile(p);
                setDisplayName(p.name || '');
                setLocation({
                  country: p.location?.country || '',
                  region: p.location?.region || '',
                });
              });
            }}
            style={btnStyle}
          >
            Cancel
          </button>
        </div>
      </div>

      <div className="sec-head">Notifications</div>
      <div className="settings-card" style={{ padding: cardPad }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div style={{ paddingRight: 16 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--white)' }}>Campaign Updates</div>
            <div style={{ fontSize: 11, color: '#555', marginTop: 2 }}>Get notified about campaign changes</div>
          </div>
          <Toggle
            label="Campaign Updates"
            checked={notifications.campaignUpdates}
            onChange={(v) => saveNotifications({ ...notifications, campaignUpdates: v })}
          />
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div style={{ paddingRight: 16 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--white)' }}>Payment Alerts</div>
            <div style={{ fontSize: 11, color: '#555', marginTop: 2 }}>Get notified when payments are ready or sent</div>
          </div>
          <Toggle
            label="Payment Alerts"
            checked={notifications.paymentAlerts}
            onChange={(v) => saveNotifications({ ...notifications, paymentAlerts: v })}
          />
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ paddingRight: 16 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--white)' }}>Weekly Digest</div>
            <div style={{ fontSize: 11, color: '#555', marginTop: 2 }}>Receive a weekly campaign and payment summary</div>
          </div>
          <Toggle
            label="Weekly Digest"
            checked={notifications.weeklyDigest}
            onChange={(v) => saveNotifications({ ...notifications, weeklyDigest: v })}
          />
        </div>
      </div>
    </>
  );
}
